// AriaCommandBar — global Cmd+K command interface.
//
// Master Directive: user can activate ARIA from anywhere, type or
// speak queries like "Who should I call?" / "Why is my revenue down?"
// ARIA responds by manipulating the OS (opening surfaces), not with
// paragraphs.
//
// This ships the SHELL — the overlay UI + keyboard bindings + input.
// The intelligence backend (invoke() → real ARIA response) is a
// separate deferred piece; for now invoke() returns a stub response.
// Consumers should feel the shell is live even before intelligence lands.
//
// Presentation: the launcher (AriaShell, `.aria-shell-dock`) morphs into
// this panel on open — corner-anchored bottom-right, directly above the
// launcher, instead of a centered scrim modal. The intelligence layer
// (invoke() → POST /api/ai/aria-command) and the Cmd+K binding below are
// untouched by this — only the container's position/size/transition.
//
// Design principles (modern-web-design):
//   - Glassmorphism + depth on the panel surface.
//   - Micro-interactions: button press feedback, focus ring, list-row
//     hover / active states.
//   - Fluid typography: clamp() for the input font-size.
//   - Accessibility: role=dialog + aria-modal, focus trap on open,
//     Esc closes, outside-click closes, prefers-reduced-motion respected.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { sixfigure, textStyle, motionDuration } from '../lib/tokens';
import { useAria } from './AriaContext';

// Seed suggestions — these are the vision's example commands.
// Downstream: replaces with ARIA-ranked contextual actions.
const SUGGESTIONS = [
  { label: 'Who should I call?',            hint: 'Open the ranked call queue' },
  { label: 'Show my hottest leads',         hint: 'Filter Leads by highest buying intent' },
  { label: 'Why is my revenue down?',       hint: 'Open Revenue Intelligence' },
  { label: 'Find missing commissions',      hint: 'Open Commission reconciliation' },
  { label: 'Who is likely to cancel?',      hint: 'Open Client Risk' },
  { label: "What happened while I was gone?", hint: 'Open Live Operations' },
  { label: 'How much did I make this week?', hint: 'Open Revenue Intelligence' },
];

// Panel is corner-anchored, fixed above the `.aria-shell-dock` launcher
// (bottom: launcher height + gap; right: same inset as the dock) so the
// launcher visually "grows" into it on open. Independent of document
// flow, so opening never shifts the page.
const CSS = `
@keyframes aria-cmdbar-panel-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.aria-cmdbar-panel {
  position: fixed;
  right: max(24px, env(safe-area-inset-right));
  bottom: calc(max(24px, env(safe-area-inset-bottom)) + 84px);
  width: min(360px, calc(100vw - 48px));
  max-width: calc(100vw - 48px);
  box-sizing: border-box;
  animation: aria-cmdbar-panel-in 220ms cubic-bezier(0.2, 0.0, 0.0, 1.0) forwards;
  transform-origin: bottom right;
}
@media (max-width: 640px) {
  .aria-cmdbar-panel {
    right: max(16px, env(safe-area-inset-right));
    bottom: calc(max(16px, env(safe-area-inset-bottom)) + 84px);
    width: calc(100vw - 32px);
    max-width: calc(100vw - 32px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .aria-cmdbar-panel { animation: none; opacity: 1; transform: none; }
}
`;

export default function AriaCommandBar() {
  const { commandBarOpen, closeCommandBar, invoke } = useAria();
  const [query, setQuery] = useState('');
  const [reply, setReply] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  // Esc closes the bar. NOTE: ⌘K is intentionally NOT bound here anymore
  // — the unified CommandPalette owns ⌘K (instant fuzzy jump + an "Ask
  // ARIA" row for natural language), so a single keystroke no longer
  // opens two overlays. This ARIA panel still opens from its dock
  // launcher (AriaShell) and via the palette's Ask ARIA hand-off.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && commandBarOpen) {
        e.preventDefault();
        closeCommandBar();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [commandBarOpen, closeCommandBar]);

  // Focus the input when the bar opens.
  useEffect(() => {
    if (commandBarOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), motionDuration('standard') + 20);
      return () => clearTimeout(t);
    } else {
      setQuery('');
      setReply(null);
    }
  }, [commandBarOpen]);

  // Outside-click closes the panel. No full-screen scrim anymore (this
  // is a corner-anchored panel, not a centered modal) — instead treat
  // any pointerdown outside the panel itself AND outside the launcher
  // dock (`.aria-shell-dock`, so clicking the launcher never fights
  // this listener) as a close.
  useEffect(() => {
    if (!commandBarOpen) return undefined;
    const onPointerDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (e.target.closest?.('.aria-shell-dock')) return;
      closeCommandBar();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [commandBarOpen, closeCommandBar]);

  const run = useCallback(async (text) => {
    const q = (text || query).trim();
    if (!q || busy) return;
    setBusy(true);
    try {
      const r = await invoke(q);
      setReply(r);
    } finally {
      setBusy(false);
    }
  }, [query, busy, invoke]);

  if (!commandBarOpen) return null;

  return (
    <>
      <style>{CSS}</style>
      {/* Panel — corner-anchored above the launcher (see CSS above).
          No full-screen scrim; outside-click is handled by the
          pointerdown listener registered above instead. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="ARIA command bar"
        className="aria-cmdbar-panel"
        style={{
          display: 'flex',
          flexDirection: 'column',
          // Bounded so the panel never grows past the top of the
          // viewport (and never covers the header / ARIA avatar there),
          // regardless of how tall the reply content is.
          maxHeight: 'min(560px, calc(100vh - 160px))',
          background: sixfigure.commandBar.background,
          borderRadius: sixfigure.radius.modal,
          border: `1px solid ${sixfigure.commandBar.borderTop}`,
          boxShadow: sixfigure.glow.intelligence,
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        {/* Input */}
        <div style={{
          flex: '0 0 auto',
          padding: sixfigure.space.md,
          borderBottom: `1px solid ${sixfigure.commandBar.borderTop}`,
          display: 'flex', alignItems: 'center', gap: sixfigure.space.sm,
        }}>
          <span aria-hidden="true" style={{
            width: 34, height: 34, borderRadius: sixfigure.radius.pill,
            background: sixfigure.accent.ai.fg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 14,
            boxShadow: sixfigure.glow.ariaActive,
          }}>✦</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); run(); }
            }}
            placeholder="Ask ARIA anything, or start a command…"
            style={{
              flex: 1,
              background: sixfigure.commandBar.inputBg,
              color: '#f8fafc',
              border: 'none',
              outline: 'none',
              borderRadius: sixfigure.radius.input,
              padding: '10px 14px',
              fontSize: 'clamp(14px, 1.6vw, 17px)',
              fontFamily: sixfigure.type?.body || 'inherit',
            }}
          />
          <span style={{
            ...textStyle('caption'),
            color: sixfigure.commandBar.placeholder,
            padding: '4px 8px',
            background: sixfigure.commandBar.shortcutBg,
            borderRadius: 6,
            fontFamily: sixfigure.type?.mono || 'ui-monospace, monospace',
          }}>
            {navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K'}
          </span>
        </div>

        {/* Reply or Suggestions */}
        <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          {reply ? (
            <div style={{ padding: sixfigure.space.lg }}>
              <div style={{ ...textStyle('eyebrow'), color: sixfigure.accent.ai.fg, marginBottom: 8 }}>
                ARIA
              </div>
              <div style={{ ...textStyle('ariaSpeech'), color: '#f8fafc' }}>
                {reply.text}
              </div>
              {reply.actions?.length > 0 && (
                <div style={{ display: 'flex', gap: sixfigure.space.sm, marginTop: sixfigure.space.md, flexWrap: 'wrap' }}>
                  {reply.actions.map((a, i) => (
                    <button
                      key={i}
                      onClick={a.onClick}
                      style={{
                        padding: '8px 14px',
                        background: sixfigure.accent.ai.fg,
                        color: '#fff',
                        border: 'none',
                        borderRadius: sixfigure.radius.button,
                        cursor: 'pointer',
                        ...textStyle('caption'),
                        fontWeight: 600,
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: `${sixfigure.space.sm}px 0` }}>
              <div style={{ ...textStyle('eyebrow'), color: sixfigure.commandBar.placeholder, padding: `0 ${sixfigure.space.lg}px`, marginBottom: 8 }}>
                TRY
              </div>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => run(s.label)}
                  disabled={busy}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 2,
                    width: '100%', boxSizing: 'border-box',
                    padding: `${sixfigure.space.sm}px ${sixfigure.space.lg}px`,
                    background: 'transparent', border: 'none',
                    color: '#f8fafc', textAlign: 'left', cursor: 'pointer',
                    transition: `background ${motionDuration('quick')}ms ${sixfigure.motion.easing.standard}`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = sixfigure.commandBar.resultHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ ...textStyle('body') }}>{s.label}</span>
                  <span style={{ ...textStyle('caption'), color: sixfigure.commandBar.placeholder }}>
                    {s.hint}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: `${sixfigure.space.sm}px ${sixfigure.space.lg}px`,
          borderTop: `1px solid ${sixfigure.commandBar.borderTop}`,
          display: 'flex', gap: sixfigure.space.md,
          ...textStyle('caption'),
          color: sixfigure.commandBar.placeholder,
        }}>
          <span>↵ Send</span>
          <span>Esc Close</span>
          <span style={{ marginLeft: 'auto' }}>{busy ? 'ARIA thinking…' : 'ARIA ready'}</span>
        </div>
      </div>
    </>
  );
}
